import React, { useEffect, useCallback, useMemo, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  RefreshControl,
  Alert,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useNotificationStore, Notification, isClientNotification, isSellerNotification } from '../store/notificationStore';
import { notificationService } from '../services/notificationService';
import { useAuthStore, useStoreStore } from '../store';
import { NotificationItem } from '../components/NotificationItem';
import { EmptyState } from '../components/EmptyState';
import { useTheme } from '../hooks/useTheme';
import { errorHandler, ErrorCategory, ErrorSeverity } from '../utils/errorHandler';
import { RootStackParamList } from '../navigation/types';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type NotificationsScreenRouteProp = RouteProp<RootStackParamList, 'Notifications'>;

export const NotificationsScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<NotificationsScreenRouteProp>();
  const { user } = useAuthStore();
  const { store } = useStoreStore();
  const [refreshing, setRefreshing] = useState(false);
  const themeContext = useTheme();
  const COLORS = themeContext.getColor;
  const SPACING = themeContext.spacing;
  const FONT_SIZE = themeContext.fontSize;

  // The context comes from the route params when navigating.
  // We default to 'client' if not provided to handle legacy navigations safely.
  const context = (route.params as any)?.context as 'client' | 'seller' || 'client';

  const styles = useMemo(() => getStyles(themeContext), [themeContext]);

  const {
    notifications,
    clientUnreadCount,
    sellerUnreadCount,
    setNotifications,
    markAsRead,
    markAllAsRead,
    clearAll,
  } = useNotificationStore();

  const visibleNotifications = useMemo(() => {
    return notifications.filter(n => {
      if (context === 'client') return isClientNotification(n);
      if (context === 'seller') {
        if (!isSellerNotification(n)) return false;
        if (store?.id && n.store_id) {
          return String(n.store_id) === String(store.id);
        }
        return true;
      }
      return true;
    });
  }, [notifications, context, store?.id]);

  const currentUnreadCount = useMemo(() => {
    if (context === 'client') return clientUnreadCount;
    return visibleNotifications.filter(n => !n.read).length;
  }, [context, clientUnreadCount, visibleNotifications]);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const storeId = context === 'seller' ? store?.id : null;
      const data = await notificationService.getByUser(user.id, context, storeId);
      setNotifications(data);
    } catch (error) {
      errorHandler.handleDatabaseError(error as Error, 'Error loading notifications:');
    }
  }, [user, context, setNotifications]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleNotificationPress = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await notificationService.markAsRead(notification.id);
        markAsRead(notification.id);
      } catch (e) {
        errorHandler.handleDatabaseError(e as Error, 'mark notification as read');
      }
    }

    const data = (notification.data || {}) as Record<string, any>;
    const orderId = data.orderId ? String(data.orderId) : null;
    const productId = data.productId ? String(data.productId) : null;
    const storeId = (data.storeId || notification.store_id) ? String(data.storeId || notification.store_id) : null;
    const action = data.action ? String(data.action) : null;

    const isSellerContext = Boolean(user?.id && store?.user_id && String(store.user_id) === String(user.id));

    if (action === 'return_requested' && isSellerContext) {
      navigation.navigate('SellerReturns');
      return;
    }

    if (orderId) {
      navigation.navigate(isSellerContext ? 'SellerOrderDetail' : 'ClientOrderDetail', { orderId });
      return;
    }

    if (productId) {
      navigation.navigate('ProductDetail', { productId });
      return;
    }

    if (storeId) {
      navigation.navigate('StoreDetail', { storeId });
      return;
    }
  };

  const renderItem = ({ item }: { item: Notification }) => (
    <NotificationItem
      notification={item}
      onPress={() => handleNotificationPress(item)}
    />
  );

  const handleMarkAllRead = useCallback(async () => {
    if (!user) return;
    try {
      await notificationService.markAllAsRead(user.id, context, context === 'seller' ? store?.id : null);
      markAllAsRead(context);
    } catch (e) {
      errorHandler.handleDatabaseError(e as Error, 'mark all as read');
    }
  }, [user, markAllAsRead, context]);

  const handleClearAll = useCallback(async () => {
    if (!user) return;
    
    // Sur le Web, Alert.alert avec boutons personnalisés peut être capricieux. On utilise window.confirm.
    if (Platform.OS === 'web') {
      if (window.confirm('Supprimer toutes les notifications ?')) {
        try {
          await notificationService.deleteAllByUser(user.id, context, context === 'seller' ? store?.id : null);
          clearAll(context);
        } catch (e) {
          errorHandler.handleDatabaseError(e as Error, 'clear notifications');
        }
      }
    } else {
      Alert.alert(
        'Effacer',
        'Supprimer toutes les notifications ?',
        [
          { text: 'Annuler', style: 'cancel' },
          {
            text: 'Effacer',
            style: 'destructive',
            onPress: async () => {
              try {
                await notificationService.deleteAllByUser(user.id, context, context === 'seller' ? store?.id : null);
                clearAll(context);
              } catch (e) {
                errorHandler.handleDatabaseError(e as Error, 'clear notifications');
              }
            },
          },
        ]
      );
    }
  }, [user, clearAll, context]);

  const onRefresh = useCallback(() => {
    const run = async () => {
      setRefreshing(true);
      await loadNotifications();
      setRefreshing(false);
    };
    run();
  }, [loadNotifications]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={COLORS.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Notifications {context === 'seller' ? 'Vendeur' : 'Client'}</Text>
        <View style={styles.headerActions}>
          {currentUnreadCount > 0 && (
            <TouchableOpacity
              style={styles.markAllButton}
              onPress={handleMarkAllRead}
            >
              <Text style={styles.markAllText}>Tout marquer lu</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.markAllButton}
            onPress={handleClearAll}
          >
            <Text style={styles.markAllText}>Clear</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Unread Count Badge */}
      {currentUnreadCount > 0 && (
        <View style={styles.badgeContainer}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{currentUnreadCount} non lu(s)</Text>
          </View>
        </View>
      )}

      {/* Notifications List */}
      <FlatList
        data={visibleNotifications}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={COLORS.accent}
          />
        }
        ListEmptyComponent={
          <EmptyState
            icon="notifications-outline"
            title="Aucune notification"
            description="Vous n'avez pas encore de notifications"
          />
        }
      />
    </View>
  );
};

const getStyles = (theme: any) => {
  const COLORS = theme.getColor;
  const SPACING = theme.spacing;
  const RADIUS = theme.radius;
  const FONT_SIZE = theme.fontSize;
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: COLORS.bg,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: SPACING.lg,
      paddingTop: SPACING.xxl,
      paddingBottom: SPACING.lg,
    },
    backButton: {
      marginRight: SPACING.md,
    },
    headerTitle: {
      flex: 1,
      fontSize: FONT_SIZE.xl,
      fontWeight: '700',
      color: COLORS.text,
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    markAllButton: {
      paddingHorizontal: SPACING.md,
      paddingVertical: SPACING.sm,
    },
    markAllText: {
      fontSize: FONT_SIZE.sm,
      color: COLORS.accent,
      fontWeight: '600',
    },
    badgeContainer: {
      paddingHorizontal: SPACING.lg,
      marginBottom: SPACING.md,
    },
    badge: {
      backgroundColor: COLORS.accent + '20',
      paddingHorizontal: SPACING.md,
      paddingVertical: SPACING.xs,
      borderRadius: 20,
      alignSelf: 'flex-start',
    },
    badgeText: {
      fontSize: FONT_SIZE.sm,
      color: COLORS.accent,
      fontWeight: '600',
    },
    listContent: {
      paddingHorizontal: SPACING.lg,
      paddingBottom: SPACING.xxl,
    },
  });
};

